import { useGetApks } from '../../api-http/apk';
import miniLogo from '../../assets/vendor/mini-logo.png';
import { APK_UPLOAD_ACCESS_USER } from '../../configs';
import { useAuth } from '../../context/auth-context';
import { metaTitle } from '../../util/tabTitle';
import { UserRole } from '../../types/user.types';
import { useIsUserRole } from '../../util/useIsUserRole';
import ApkTable from './ApkTable';
import ApkUploadForm from './ApkUploadForm';
import {
  Box,
  Button,
  CardActionArea,
  Chip,
  Collapse,
  Divider,
  Stack,
} from '@mui/material';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import React, { FC, useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  FilePlus,
  Film,
  Layers,
  Monitor,
} from 'react-feather';
import { useNavigate } from 'react-router-dom';

const STAGING_APK_BASE_URL =
  'https://stec.fra1.digitaloceanspaces.com/advertex-v3/advertex3/stage/apk';

const STAGING_APPS = [
  {
    label: 'MobileApp (Staging)',
    fileName: 'app-staging.apk',
  },
  {
    label: 'TVApp (Staging)',
    fileName: 'SignageTec-TVApp-staging-v2.0-debug.apk',
  },
];

const QUICK_LINKS = [
  {
    title: 'Presentations',
    description: 'Create and manage presentations for your displays.',
    href: '/presentations',
    icon: BookOpen,
    accent: '#1f6feb',
    tint: '#eaf2ff',
  },
  {
    title: 'Slides',
    description: 'Create reusable slides with media and widgets.',
    href: '/slides',
    icon: Film,
    accent: '#16a66a',
    tint: '#e8f8f0',
  },
  {
    title: 'Devices',
    description: 'Manage and monitor your devices.',
    href: '/devices',
    icon: Monitor,
    accent: '#e8750a',
    tint: '#fff1e4',
  },
  {
    title: 'Scheduler',
    description: 'Plan and automate content playback.',
    href: '/scheduler',
    icon: Clock,
    accent: '#5646d8',
    tint: '#efedff',
  },
];

const GETTING_STARTED_STEPS = [
  {
    title: 'Create your content',
    description: 'Upload assets and turn them into reusable slides.',
    icon: FilePlus,
    accent: '#1f6feb',
    tint: '#eaf2ff',
  },
  {
    title: 'Build a presentation',
    description: 'Arrange your slides into the story you want to show.',
    icon: Layers,
    accent: '#16a66a',
    tint: '#e8f8f0',
  },
  {
    title: 'Schedule and publish',
    description: 'Choose your displays and decide when content should play.',
    icon: Calendar,
    accent: '#e8750a',
    tint: '#fff1e4',
  },
];

const HomePage: FC = () => {
  metaTitle('Home');

  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isSuperAdmin = useIsUserRole([UserRole.superAdmin]);

  const { refetch, error, data: apksData, isLoading } = useGetApks();

  const [apkHistoryOpen, setApkHistoryOpen] = useState(false);

  return (
    <Box sx={{ maxWidth: 1360, mx: 'auto' }}>
      <Card
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid rgba(35, 48, 68, 0.10)',
          boxShadow: '0 2px 8px rgba(35, 48, 68, 0.08)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) 1fr' },
            minHeight: { md: 190 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              p: { xs: 5, md: 7 },
            }}
          >
            <Typography
              component="h2"
              variant="h5"
              sx={{ mb: 1.5, fontWeight: 700, color: '#1f2937' }}
            >
              Welcome back{currentUser?.name ? `, ${currentUser.name}` : ''}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ maxWidth: 580, lineHeight: 1.6 }}
            >
              Create, schedule, and manage digital signage content from one
              place.
            </Typography>
            <Button
              variant="contained"
              endIcon={<ArrowRight size={17} />}
              onClick={() => navigate('/presentations')}
              sx={{ mt: 4, px: 4, alignSelf: 'flex-start' }}
            >
              Get started
            </Button>
          </Box>

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              overflow: 'hidden',
              p: { xs: 5, md: 6 },
              pl: { md: 7 },
              borderLeft: { md: '1px solid rgba(35, 48, 68, 0.10)' },
              borderTop: { xs: '1px solid rgba(35, 48, 68, 0.10)', md: 0 },
            }}
          >
            <Box
              component="img"
              src={miniLogo}
              alt=""
              aria-hidden="true"
              sx={{
                position: 'absolute',
                width: 230,
                height: 230,
                objectFit: 'contain',
                right: -58,
                bottom: -72,
                opacity: 0.12,
                pointerEvents: 'none',
              }}
            />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ position: 'relative', fontWeight: 700, letterSpacing: 1 }}
            >
              Organization
            </Typography>
            <Typography
              variant="h6"
              sx={{ position: 'relative', mt: 0.5, fontWeight: 700 }}
            >
              {currentUser?.organization?.name || 'Signage Tech'}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ position: 'relative', mt: 1 }}
            >
              Codename: {currentUser?.organization?.pinCode || '—'}
            </Typography>
          </Box>
        </Box>
      </Card>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 3,
          mb: 4,
        }}
      >
        {QUICK_LINKS.map(
          ({ title, description, href, icon: Icon, accent, tint }) => (
            <Card
              key={href}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: 'rgba(35, 48, 68, 0.12)',
                boxShadow: '0 1px 4px rgba(35, 48, 68, 0.06)',
                transition:
                  'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  borderColor: accent,
                  boxShadow: '0 8px 22px rgba(35, 48, 68, 0.10)',
                },
              }}
            >
              <CardActionArea
                onClick={() => navigate(href)}
                sx={{
                  height: '100%',
                  p: 5,
                  display: 'flex',
                  alignItems: 'stretch',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: tint,
                      color: accent,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={22} />
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ mt: 3, fontWeight: 700 }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, mb: 3, minHeight: 42, lineHeight: 1.5 }}
                  >
                    {description}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 'auto', color: accent }}
                  >
                    <Typography variant="button">Go to {title}</Typography>
                    <ArrowRight size={16} />
                  </Stack>
                </Box>
              </CardActionArea>
            </Card>
          ),
        )}
      </Box>

      <Card
        variant="outlined"
        sx={{
          mb: 4,
          p: { xs: 5, md: 6 },
          borderRadius: 2,
          borderColor: 'rgba(35, 48, 68, 0.12)',
          boxShadow: '0 1px 4px rgba(35, 48, 68, 0.06)',
        }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          Getting started
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 5 }}>
          Follow these steps to get your digital signage up and running.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: { xs: 5, md: 3 },
          }}
        >
          {GETTING_STARTED_STEPS.map(
            ({ title, description, icon: Icon, accent, tint }, index) => (
              <Box
                key={title}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  gap: 3,
                  pr: { md: index < GETTING_STARTED_STEPS.length - 1 ? 4 : 0 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 36px',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: tint,
                    color: accent,
                    fontWeight: 700,
                    zIndex: 1,
                  }}
                >
                  {index + 1}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: accent,
                      mb: 2,
                    }}
                  >
                    <Icon size={24} />
                    {index < GETTING_STARTED_STEPS.length - 1 && (
                      <Box
                        sx={{
                          display: { xs: 'none', md: 'block' },
                          height: 1,
                          flex: 1,
                          ml: 3,
                          bgcolor: 'rgba(35, 48, 68, 0.14)',
                        }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, lineHeight: 1.6 }}
                  >
                    {description}
                  </Typography>
                </Box>
              </Box>
            ),
          )}
        </Box>
      </Card>

      {isSuperAdmin && (
        <Card
          variant="outlined"
          sx={{
            mt: 2,
            mb: 4,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            borderColor: '#e2e8f0',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            gap={2}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography component="h2" variant="h6">
                  App downloads
                </Typography>
                <Chip
                  label="Super admin"
                  size="small"
                  sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 700 }}
                />
              </Stack>
              <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                Download current device apps or review earlier APK releases.
              </Typography>
            </Box>
            {apksData?.data[0] && (
              <Button
                component="a"
                href={apksData.data[0].key}
                download
                variant="contained"
                startIcon={<Download size={16} />}
              >
                Download latest
              </Button>
            )}
          </Stack>

          <Divider sx={{ my: 3 }} />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>Staging builds</Typography>
            <Chip
              label="Staging environment only"
              color="warning"
              size="small"
            />
          </Stack>
          <Typography sx={{ mb: 2 }} color="text.secondary">
            These builds connect only to staging. Do not install them on
            production devices.
          </Typography>
          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
            {STAGING_APPS.map(({ label, fileName }) => (
              <Button
                key={fileName}
                variant="outlined"
                startIcon={<Download size={16} />}
                href={`${STAGING_APK_BASE_URL}/${fileName}`}
              >
                {label}
              </Button>
            ))}
          </Stack>

          {currentUser?.email === APK_UPLOAD_ACCESS_USER && (
            <ApkUploadForm onFinished={refetch} />
          )}

          <Divider sx={{ my: 3 }} />
          <Button
            variant="text"
            onClick={() => setApkHistoryOpen((open) => !open)}
            endIcon={
              apkHistoryOpen ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )
            }
            sx={{ px: 0.5, color: '#475569' }}
          >
            Older versions
          </Button>
          <Collapse in={apkHistoryOpen}>
            <Box sx={{ pt: 2 }}>
              <ApkTable data={apksData} error={error} isLoading={isLoading} />
            </Box>
          </Collapse>
        </Card>
      )}
    </Box>
  );
};

export default HomePage;
